import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Terms & Conditions · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Terms and conditions for booking performances and using the Surer Adda With Sanchayeeta website." },
      { property: "og:title", content: "Terms & Conditions · Surer Adda" },
      { property: "og:description", content: "Booking, cancellation, performance and liability terms for engaging Sanchayeeta Seal." },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/terms` }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">Terms &amp; Conditions</p>
        <p className="mt-1 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-foreground text-sm leading-relaxed">
          <p>
            Welcome to Surer Adda With Sanchayeeta. By accessing and using this website, you accept
            and agree to comply with the following Terms and Conditions. If you do not agree with
            any part of these terms, please do not use this website.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Use of Website</h2>
          <p>
            This website is intended to provide information related to music, live performances,
            song covers, event bookings, updates, and general content associated with Surer Adda
            With Sanchayeeta.
          </p>
          <p>
            By using this website, you agree to use it only for lawful purposes and in a manner
            that does not harm, disrupt, misuse, or negatively affect the website, its content, or
            other users.
          </p>
          <p>You agree not to:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Use the website for unlawful or harmful activities</li>
            <li>Attempt unauthorized access to the website or its systems</li>
            <li>Copy, republish, or misuse website content without permission</li>
            <li>Submit false, misleading, abusive, or inappropriate information through forms or contact methods</li>
            <li>Interfere with the website's functionality, security, or performance</li>
          </ul>

          <h2 className="mt-6 text-lg font-semibold">Intellectual Property</h2>
          <p>
            Unless otherwise stated, all content published on Surer Adda With Sanchayeeta, including
            text, graphics, branding elements, music-related promotional content, logos, design
            elements, and website materials, belongs to the website owner or is used with
            appropriate rights/permission where applicable.
          </p>
          <p>
            Unauthorized copying, reproduction, distribution, or commercial use of website content
            without permission is not allowed.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Content Accuracy</h2>
          <p>
            We make reasonable efforts to keep the content on this website accurate and updated.
            However, we do not guarantee that all information will always be complete, current, or
            error-free. Content may be modified, updated, or removed at any time without prior
            notice.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Booking and Enquiry Information</h2>
          <p>
            If this website allows visitors to submit event booking requests or enquiries,
            submitting an enquiry does not automatically confirm a booking. All bookings are
            subject to direct communication, availability, mutual agreement, and final confirmation.
          </p>
          <p>
            We reserve the right to accept, decline, or modify booking-related discussions based on
            schedule availability, event suitability, or operational limitations.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Third-Party Links</h2>
          <p>
            This website may contain links to third-party websites, platforms, or social media
            pages. These links are provided for convenience and informational purposes only. We do
            not control or guarantee the content, services, or privacy practices of third-party
            websites.
          </p>
          <p>Visiting third-party websites from this site is done at your own discretion.</p>

          <h2 className="mt-6 text-lg font-semibold">Limitation of Liability</h2>
          <p>
            By using this website, you understand and agree that Surer Adda With Sanchayeeta shall
            not be held liable for any direct, indirect, incidental, or consequential loss or
            damage resulting from:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Use of or inability to use the website</li>
            <li>Reliance on website content</li>
            <li>Technical interruptions, website errors, or temporary unavailability</li>
            <li>Third-party links, advertisements, or external services</li>
          </ul>

          <h2 className="mt-6 text-lg font-semibold">Privacy</h2>
          <p>
            Your use of this website is also subject to our{" "}
            <a className="text-primary hover:underline" href="/privacy">Privacy Policy</a>. We
            encourage all users to read the Privacy Policy to understand how information may be
            collected and used.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Changes to Terms</h2>
          <p>
            We may revise or update these Terms and Conditions at any time without prior notice. By
            continuing to use the website after changes are made, you accept the updated terms.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Governing Use</h2>
          <p>
            These Terms and Conditions are intended to govern the use of this website in a fair,
            lawful, and respectful manner.
          </p>

          <h2 className="mt-6 text-lg font-semibold">Contact Us</h2>
          <p>
            If you have any questions about these Terms and Conditions, you may contact us through
            the details provided on the{" "}
            <a className="text-primary hover:underline" href="/contact">Contact Us</a> page.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
